// Download helpers for an OCR result — no third-party libraries.
//
//   JSON  -> the raw structured data, for developers / re-import.
//   CSV   -> Excel-friendly: a Field/Value block plus one block per table.
//   Print -> the browser print dialog, from which the user picks "Save as PDF".
//
// CSV and JSON reuse the same display model as the on-screen view, so what you
// download matches what you see (empty fields included, for completeness).

import { buildDisplayModel, DisplayModel, Section } from './display';

function sanitizeFilename(name: string): string {
  const base = name.replace(/\.[^.]+$/, '').replace(/[^a-z0-9._-]+/gi, '_').replace(/^_+|_+$/g, '');
  return base || 'ocr-result';
}

function triggerDownload(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so the download has started.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function downloadJson(data: unknown, filename: string): void {
  triggerDownload(JSON.stringify(data, null, 2), `${sanitizeFilename(filename)}.json`, 'application/json');
}

// --- CSV -----------------------------------------------------------------

function csvCell(value: string): string {
  const v = value ?? '';
  if (/[",\r\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function sectionToCsv(section: Section): string[] {
  const lines: string[] = [];
  if (section.kind === 'details') {
    lines.push(csvCell(section.title));
    lines.push('Field,Value');
    for (const e of section.entries) lines.push(`${csvCell(e.label)},${csvCell(e.value)}`);
  } else if (section.kind === 'table') {
    lines.push(csvCell(section.title));
    lines.push(section.columns.map((c) => csvCell(c.label)).join(','));
    for (const row of section.rows) {
      lines.push(section.columns.map((c) => csvCell(row[c.key] ?? '')).join(','));
    }
  } else if (section.kind === 'list') {
    lines.push(csvCell(section.title));
    for (const item of section.items) lines.push(csvCell(item));
  }
  return lines;
}

export function buildCsv(model: DisplayModel): string {
  const lines: string[] = [];
  lines.push(csvCell(model.title));
  if (model.subtitle) lines.push(csvCell(model.subtitle));
  lines.push('');

  for (const section of model.sections) {
    lines.push(...sectionToCsv(section));
    lines.push('');
  }

  if (model.totals.length) {
    lines.push('Totals');
    lines.push('Field,Value');
    for (const t of model.totals) lines.push(`${csvCell(t.label)},${csvCell(t.value)}`);
    lines.push('');
  }
  // Leading BOM so Excel opens UTF-8 (accented vendor names) correctly.
  return `﻿${lines.join('\r\n')}`;
}

export function downloadCsv(data: unknown, docTypeLabel: string, filename: string): void {
  const model = buildDisplayModel(data, docTypeLabel, { includeEmpty: true });
  triggerDownload(buildCsv(model), `${sanitizeFilename(filename)}.csv`, 'text/csv');
}

// --- Print / PDF ---------------------------------------------------------

/**
 * Print a single result card via the browser (the user chooses "Save as PDF").
 * We reuse the already-rendered React DOM — `@media print` in App.css hides
 * everything except the element that carries `is-printing`, so there is a
 * single source of truth for the layout.
 */
export function printElement(el: HTMLElement | null): void {
  if (!el) {
    window.print();
    return;
  }
  const cleanup = () => {
    el.classList.remove('is-printing');
    document.body.classList.remove('printing');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  document.body.classList.add('printing');
  el.classList.add('is-printing');
  // Let the class apply before the (synchronous) print dialog blocks paint.
  window.requestAnimationFrame(() => window.print());
}
